import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { disablePush, enablePush, isIOS, pushSupported } from "@/lib/push";

const read = (k, d) => (localStorage.getItem(k) === null ? d : localStorage.getItem(k) === "1");

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.14);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.14 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.14 + 0.18);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.14); o.stop(ctx.currentTime + i * 0.14 + 0.2);
    });
  } catch { /* audio not available */ }
};

export const useNotifications = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const since = useRef(null);
  const [desktop, setDesktop] = useState(() => read("notif_desktop", false) && "Notification" in window && Notification.permission === "granted");
  const [sound, setSound] = useState(() => read("notif_sound", true));
  const [push, setPush] = useState(() => read("notif_push", false));
  const prefs = useRef({ desktop, sound, push });
  prefs.current = { desktop, sound, push };

  useEffect(() => {
    if (desktop && push && pushSupported()) enablePush().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    const notify = (title, body, cid) => {
      const open = () => navigate(`/inbox?c=${cid}`);
      toast(title, { description: body, action: { label: "Apri", onClick: open } });
      if (prefs.current.desktop && !prefs.current.push && document.hidden) {
        const n = new Notification(title, { body, tag: cid, icon: "/favicon.ico" });
        n.onclick = () => { window.focus(); open(); n.close(); };
      }
    };
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { since: since.current || undefined } });
        if (!alive) return;
        const fresh = since.current !== null;
        since.current = data.now;
        if (!fresh) return;
        const relevant = data.messages.filter((m) => m.mine);
        relevant.slice(-3).forEach((m) => notify(`Nuovo messaggio da ${m.name}`, m.text, m.conversation_id));
        data.assigned.forEach((a) => notify("Chat assegnata a te", `${a.name} · assegnata da ${a.by}`, a.conversation_id));
        const low = data.low_ratings || [];
        low.forEach((r) => {
          const open = () => navigate(`/inbox?c=${r.conversation_id}`);
          toast.error(`Voto basso: ${r.rating}/5 da ${r.name}`, { description: `Agente: ${r.agent_name || "—"} · ricontatta il cliente`, duration: 20000, action: { label: "Ricontatta", onClick: open } });
          if (prefs.current.desktop && !prefs.current.push) {
            const n = new Notification(`Voto basso: ${r.rating}/5`, { body: `${r.name} ha dato una valutazione negativa. Ricontattalo.`, tag: `low-${r.id}`, icon: "/icon-192.png", requireInteraction: true });
            n.onclick = () => { window.focus(); open(); n.close(); };
          }
        });
        if (low.length) qc.invalidateQueries({ queryKey: ["low-ratings"] });
        (data.deletions || []).forEach((d) => {
          toast.warning(`Richiesta eliminazione dati da ${d.name}`, { duration: 20000, action: { label: "Apri", onClick: () => navigate(`/inbox?c=${d.conversation_id}`) } });
        });
        if ((data.deletions || []).length) qc.invalidateQueries({ queryKey: ["deletion-requests"] });
        if (relevant.length || data.assigned.length || low.length) {
          if (prefs.current.sound) beep();
          qc.invalidateQueries({ queryKey: ["conversations"] });
        }
      } catch { /* retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [navigate, qc]);

  const toggleDesktop = async () => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isIOS() && !standalone) return toast.info("Su iPhone/iPad installa prima l'app: Condividi → Aggiungi alla schermata Home, poi attiva le notifiche dall'app.");
    if (!("Notification" in window)) return toast.error("Il browser non supporta le notifiche");
    if (desktop) {
      setDesktop(false); setPush(false);
      localStorage.setItem("notif_desktop", "0"); localStorage.setItem("notif_push", "0");
      await disablePush();
      return toast.success("Notifiche disattivate su questo dispositivo");
    }
    const p = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
    if (p !== "granted") return toast.error("Permesso notifiche negato. Abilitalo dalle impostazioni del browser.");
    setDesktop(true); localStorage.setItem("notif_desktop", "1");
    if (pushSupported()) {
      try {
        await enablePush();
        setPush(true); localStorage.setItem("notif_push", "1");
        await api.post("/push/test");
        return toast.success("Notifiche attive anche con l'app chiusa");
      } catch {
        toast.warning("Notifiche push non disponibili: riceverai avvisi solo con l'app aperta");
      }
    }
    toast.success("Notifiche desktop attivate");
  };
  const toggleSound = () => {
    const v = !sound; setSound(v); localStorage.setItem("notif_sound", v ? "1" : "0");
    if (v) beep();
  };
  return { desktop, sound, toggleDesktop, toggleSound };
};
